// Sub-componente: Histórico de eventos da parcela (Sprint F4).
// Exibe timeline (mais recente primeiro), com diff expandido por evento
// (campo: antes → depois) e botão ↶ Reverter pra desfazer edições.
// Desde 2026-09-02 a timeline também aceita COMENTÁRIOS com @menção (padrão
// de Atividades: antd <Mentions>, @email no texto, sino pra quem foi
// mencionado). Comentário é um evento tipo 'comentario' na mesma tabela —
// entra na ordem cronológica e a reversão já o ignora.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Mentions } from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { extrairMencoes } from '../atividades/utils';

// Mesmo regex canônico de Atividades (pages/atividades/utils.ts) — usado só
// pra DESTACAR o @email no texto exibido (o extrair é importado de lá).
const MENCAO_RE = /(@[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
function TextoComMencoes({ texto }: { texto: string }) {
  const partes = String(texto || '').split(MENCAO_RE);
  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {partes.map((p, i) => p.match(MENCAO_RE)
        ? <strong key={i} style={{ color: '#0930D0' }}>{p}</strong>
        : <span key={i}>{p}</span>)}
    </span>
  );
}

export function HistoricoParcela({ parcelaId, onMudou }: { parcelaId: string; onMudou?: () => void }) {
  const [eventos, setEventos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [revertendoId, setRevertendoId] = useState<string | null>(null);
  const [expandido, setExpandido] = useState<Record<string, boolean>>({});
  // Comentários (2026-09-02)
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [excluindoId, setExcluindoId] = useState<string | null>(null);
  const [meuEmail, setMeuEmail] = useState('');
  const [usuarios, setUsuarios] = useState<Array<{ email: string; nome: string }>>([]);

  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_listar_eventos_parcela', parcela_id: parcelaId },
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setEventos(d.eventos || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [parcelaId]);

  // Quem sou eu (pro botão de excluir o próprio comentário) + quem dá pra
  // mencionar (mesma fonte de Atividades: /admin/usuarios). Best-effort —
  // sem a lista, o @ ainda funciona digitando o e-mail completo.
  useEffect(() => {
    (async () => {
      try {
        const s = await fetchAuthSession();
        setMeuEmail(String((s.tokens?.idToken?.payload as any)?.email || '').toLowerCase());
      } catch { /* sem sessão o botão de excluir só não aparece */ }
      try {
        const token = await getToken();
        // ?modulo=financeiro: o autocomplete só oferece quem tem alguma
        // permissão do módulo — mencionar quem não alcança o CR gera um sino
        // com link que a pessoa não consegue abrir. (Digitar um e-mail
        // completo à mão continua valendo, de propósito.)
        const r = await axios.get(`${API_URL}/admin/usuarios?modulo=financeiro`, { headers: { Authorization: token } });
        const d = r.data.body ? JSON.parse(r.data.body) : r.data;
        setUsuarios((Array.isArray(d) ? d : [])
          .filter((u: any) => u.enabled !== false && u.email)
          .map((u: any) => ({ email: String(u.email).toLowerCase(), nome: u.nome || u.email })));
      } catch { /* autocomplete vazio, menção manual segue valendo */ }
    })();
  }, []);

  async function comentar() {
    const t = texto.trim();
    if (!t) return;
    setEnviando(true);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_comentar_parcela',
        parcela_id: parcelaId,
        texto: t,
        mencoes: extrairMencoes(t),
      }, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d?.error || d?.erro) { alert(d.error || d.erro); return; }
      setTexto('');
      // Recarga com falha própria: o comentário JÁ foi salvo — mostrar "Erro
      // ao comentar" aqui induzia a pessoa a reenviar e duplicar (comentário
      // E sino de cada mencionado).
      try { await carregar(); }
      catch { alert('Comentário salvo. Só a recarga do histórico falhou — feche e abra a aba pra vê-lo.'); }
    } catch (e: any) {
      alert('Erro ao comentar: ' + (e.response?.data?.error || e.message));
    } finally { setEnviando(false); }
  }

  async function excluirComentario(ev: any) {
    if (!window.confirm('Excluir este comentário?')) return;
    setExcluindoId(ev.id);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`,
        { action: 'fin_excluir_comentario_parcela', id: ev.id },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) {
      alert('Erro ao excluir: ' + (e.response?.data?.error || e.message));
    } finally { setExcluindoId(null); }
  }

  async function reverter(ev: any) {
    const campos = Object.keys(ev.meta || {}).filter(k =>
      ev.meta[k] && typeof ev.meta[k] === 'object' && 'de' in ev.meta[k]
    );
    const resumo = campos.map(k =>
      `  ${k}: ${ev.meta[k].para ?? '∅'} → ${ev.meta[k].de ?? '∅'}`
    ).join('\n');
    if (!window.confirm(
      `Reverter este evento?\n\nIsso vai restaurar os valores anteriores:\n${resumo}\n\n` +
      `Um novo evento "revertido" vai aparecer na timeline.`
    )) return;
    setRevertendoId(ev.id);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas-geradas`, {
        action: 'fin_reverter_evento_parcela', evento_id: ev.id,
      }, { headers: { Authorization: token } });
      await carregar();
      if (onMudou) onMudou();
    } catch (e: any) {
      alert('Erro ao reverter: ' + (e.response?.data?.error || e.message));
    } finally { setRevertendoId(null); }
  }

  const corTipo: Record<string, string> = {
    criada: '#888', status: '#0930D0', recebida: '#0a7f4a',
    liberada: '#7C3AED', aprovada: '#0a7f4a', recusada: '#B91626',
    anexo_add: '#0930D0', anexo_del: '#888', editada: '#B07500',
    revertido: '#7C3AED', comentario: '#0930D0', retencao_cliente: '#B07500',
    areas_contrato: '#0930D0',
  };
  const iconeTipo: Record<string, string> = {
    criada: '✨', status: '🔄', recebida: '💰', liberada: '🚀',
    aprovada: '✓', recusada: '✕', anexo_add: '📎', anexo_del: '🗑',
    editada: '✏️', revertido: '↶', comentario: '💬', retencao_cliente: '🧾',
    areas_contrato: '🧭',
  };
  // Tipo gravado pelo servidor sem forma de ler bem cru (21/09/2026: a troca
  // de "retém ISS" no cadastro do cliente recalcula a parcela).
  const rotuloTipo: Record<string, string> = { retencao_cliente: 'retenção do cliente', areas_contrato: 'área/CNAE do contrato' };
  const fmtCampoVal = (v: any) => {
    if (v == null || v === '') return '∅';
    if (typeof v === 'object') return JSON.stringify(v);
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) {
      return v.slice(0, 10).split('-').reverse().join('/');
    }
    return String(v);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Caixa de comentário — sempre visível, acima da timeline. */}
      <div style={{ padding: 10, background: '#fff', border: '0.5px solid #d8dcec', borderRadius: 6 }}>
        <Mentions
          value={texto}
          onChange={setTexto}
          rows={2}
          placeholder="Comente aqui. Use @ para mencionar alguém — a pessoa recebe o aviso no sino."
          options={usuarios.map(u => ({ value: u.email, label: `${u.nome} (${u.email})` }))}
          // Quem digita @mar procura "Marina", não o e-mail — o filtro casa o
          // NOME e o e-mail (o default do antd só olha o value/e-mail).
          filterOption={(input, opt) =>
            `${(opt as any)?.label || ''} ${(opt as any)?.value || ''}`
              .toLowerCase().includes(String(input || '').toLowerCase())}
          autoSize={{ minRows: 2, maxRows: 4 }}
          style={{ width: '100%' }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <span style={{ fontSize: 10, color: '#999' }}>
            A menção (@) avisa a pessoa pelo sino, com link direto pra esta parcela.
          </span>
          <button onClick={comentar} disabled={enviando || !texto.trim()}
            style={{ padding: '5px 14px', fontSize: 11, fontWeight: 600,
              background: (enviando || !texto.trim()) ? '#8FA0E8' : '#0930D0',
              color: '#fff', border: 'none', borderRadius: 4,
              cursor: (enviando || !texto.trim()) ? 'default' : 'pointer' }}>
            {enviando ? 'Enviando…' : '💬 Comentar'}
          </button>
        </div>
      </div>

      {loading && <div style={{ padding: 20, color: '#888', textAlign: 'center' }}>Carregando…</div>}
      {!loading && eventos.length === 0 && (
        <div style={{ padding: 30, color: '#aaa', textAlign: 'center', fontStyle: 'italic' }}>
          Sem histórico ainda. Cada mudança no status, recebimento, anexo, aprovação
          ou comentário aparecerá aqui.
        </div>
      )}
      {!loading && eventos.map(ev => {
        const meta = ev.meta || {};
        const ehComentario = ev.tipo === 'comentario';
        const camposDiff = Object.keys(meta).filter(k =>
          meta[k] && typeof meta[k] === 'object' && !Array.isArray(meta[k]) && 'de' in meta[k]
        );
        const podeReverter = !ev.revertido
          && ['editada', 'status'].includes(ev.tipo)
          && camposDiff.length > 0;
        const isExp = expandido[ev.id];
        const autorComentario = ehComentario
          ? (meta.autor_nome || String(ev.criado_por || '').split('@')[0]) : null;
        return (
          <div key={ev.id} style={{
            padding: 10, background: ev.revertido ? '#F4F4F8' : ehComentario ? '#F4F7FF' : '#FAFBFF',
            borderRadius: 5, borderLeft: `3px solid ${corTipo[ev.tipo] || '#888'}`,
            opacity: ev.revertido ? 0.7 : 1,
          }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#222', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13 }}>{iconeTipo[ev.tipo] || '•'}</span>
                  <span style={{ color: corTipo[ev.tipo] || '#555' }}>
                    {ehComentario ? `comentário · ${autorComentario}` : (rotuloTipo[ev.tipo] || ev.tipo)}
                  </span>
                  {ev.revertido && (
                    <span style={{
                      fontSize: 9, padding: '1px 6px', borderRadius: 8,
                      background: '#EDE4FE', color: '#7C3AED', fontWeight: 600,
                    }}>REVERTIDO</span>
                  )}
                </div>
                <div style={{ fontSize: ehComentario ? 12 : 11, color: '#333', marginTop: 3 }}>
                  {ehComentario
                    ? <TextoComMencoes texto={ev.descricao || ''} />
                    : (ev.descricao || '—')}
                </div>
                {ehComentario && Array.isArray(meta.mencoes) && meta.mencoes.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                    {meta.mencoes.map((m: string) => (
                      <span key={m} style={{ fontSize: 9.5, padding: '1px 7px', borderRadius: 8,
                        background: '#EFF3FF', color: '#0930D0' }}>🔔 {m}</span>
                    ))}
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                  {new Date(ev.criado_em).toLocaleString('pt-BR')}
                  {!ehComentario && ev.criado_por && ` · ${String(ev.criado_por).split('@')[0]}`}
                  {ev.revertido && ev.revertido_em && (
                    <> · revertido em {new Date(ev.revertido_em).toLocaleString('pt-BR')}
                      {ev.revertido_por && ` por ${String(ev.revertido_por).split('@')[0]}`}</>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                {ehComentario && meuEmail && String(ev.criado_por || '').toLowerCase() === meuEmail && (
                  <button onClick={() => excluirComentario(ev)}
                    disabled={excluindoId === ev.id}
                    title="Excluir meu comentário"
                    style={{ padding: '3px 8px', fontSize: 10, background: '#fff',
                      color: '#B91626', border: '0.5px solid #FBBABA', borderRadius: 3,
                      cursor: excluindoId === ev.id ? 'wait' : 'pointer' }}>
                    {excluindoId === ev.id ? '…' : '🗑'}
                  </button>
                )}
                {!ehComentario && camposDiff.length > 0 && (
                  <button onClick={() => setExpandido(s => ({ ...s, [ev.id]: !s[ev.id] }))}
                    style={{
                      padding: '3px 8px', fontSize: 10, background: '#fff',
                      color: '#0930D0', border: '0.5px solid #ddd', borderRadius: 3,
                      cursor: 'pointer',
                    }}>
                    {isExp ? 'Esconder' : 'Ver diff'} ({camposDiff.length})
                  </button>
                )}
                {podeReverter && (
                  <button onClick={() => reverter(ev)}
                    disabled={revertendoId === ev.id}
                    title="Restaurar valores anteriores deste evento"
                    style={{
                      padding: '3px 10px', fontSize: 10, fontWeight: 600,
                      background: '#fff', color: '#7C3AED',
                      border: '0.5px solid #7C3AED', borderRadius: 3,
                      cursor: revertendoId === ev.id ? 'wait' : 'pointer',
                    }}>
                    {revertendoId === ev.id ? 'Revertendo…' : '↶ Reverter'}
                  </button>
                )}
              </div>
            </div>
            {!ehComentario && isExp && camposDiff.length > 0 && (
              <table style={{ width: '100%', marginTop: 8, fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#888', textAlign: 'left' }}>
                    <th style={{ padding: '4px 6px', fontWeight: 500 }}>Campo</th>
                    <th style={{ padding: '4px 6px', fontWeight: 500 }}>Antes</th>
                    <th style={{ padding: '4px 6px', fontWeight: 500 }}>Depois</th>
                  </tr>
                </thead>
                <tbody>
                  {camposDiff.map(k => (
                    <tr key={k} style={{ borderTop: '0.5px solid #eee' }}>
                      <td style={{ padding: '4px 6px', color: '#444', fontWeight: 500 }}>{k}</td>
                      <td style={{ padding: '4px 6px', color: '#888' }}>{fmtCampoVal(meta[k].de)}</td>
                      <td style={{ padding: '4px 6px', color: '#222' }}>{fmtCampoVal(meta[k].para)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}
